/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */
import React, { useState } from 'react';
import { Input, FormGroup, Collapse, Button, Row, Col } from 'reactstrap';
import { useToggle } from '../../../hooks/useToggle';
import { RequestScope } from '../../../actions/api';
import classNames from 'classnames';
import { useSiteAlert } from '../../../hooks/useSiteAlert';
import {
  canAccessData,
  convertScope,
  getDataScopeSurvey,
  getScopeAccess,
  getScopeOptions,
  getScopeViews,
} from '../../../utils/dataShare';
import { MetricGroup } from '../../../types/metricGroup';
import { TOOLTIP } from './DataShareForm';
import { DataShareActions, InitiativeDataShare } from '../../../types/initiative';
import { DataScopeGroups } from './DataScopeGroups';
import { SurveyScope } from '@g17eco/types/surveyCommon';
import { useInitiativeRequestScopes } from '../hooks/useInitiativeRequestScopes';
import { PACK, SURVEY } from '@constants/terminology';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DataScopeAccess, DataScopeSurvey, DataShareScopeView } from '../../../types/surveyDataScope';

enum DataShareTarget {
  CompletedSurveys = 'completed-surveys',
  OnlyAssuredData = 'only-assured-data',
}

interface LabelProps {
  children: JSX.Element | string;
}
const Title = ({ children }: LabelProps) => (
  <label className='text-sm text-ThemeTextMedium text-uppercase lh-1'>{children}</label>
);

export interface DataShareCommonProps {
  metricGroups: MetricGroup[];
  actions: DataShareActions;
  initiativeCombinedScope: SurveyScope | undefined;
}

interface PermissionsTemplateProps extends DataShareCommonProps {
  dataShareTemplate: InitiativeDataShare;
}

export const PermissionsTemplate = ({
  dataShareTemplate,
  metricGroups,
  actions,
  initiativeCombinedScope
}: PermissionsTemplateProps) => {
  const { addSiteError } = useSiteAlert();

  const { dataScope: dataScopeTemplate } = dataShareTemplate;
  const accessData = canAccessData(dataScopeTemplate);
  const shareAllPacks = getScopeAccess(dataScopeTemplate) === DataScopeAccess.Full;
  const requestScopes = getScopeOptions(dataScopeTemplate);
  const shareScopeViews = getScopeViews(dataScopeTemplate);

  const [openSubControls, toggleSubControls, setSubControls] = useToggle(() => accessData);
  const [shareTarget, setShareTarget] = useState<DataShareTarget>(DataShareTarget.CompletedSurveys);

  const initiativeRequestScopes = useInitiativeRequestScopes({ metricGroups, initiativeCombinedScope, requestScopes });

  const updateDataScopeSurvey = async (dataScopeSurvey: DataScopeSurvey) => {
    await actions.updateTemplate({ ...dataShareTemplate, dataScope: { ...dataScopeTemplate, survey: dataScopeSurvey }});
  }

  const changeAccessData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    let dataScopeSurvey = getDataScopeSurvey(dataScopeTemplate);
    if (checked) {
      dataScopeSurvey = {
        ...dataScopeSurvey,
        access: shareAllPacks ? DataScopeAccess.Full : DataScopeAccess.Partial,
      };
      if (!shareAllPacks) {
        dataScopeSurvey = {
          ...dataScopeSurvey,
          scope: convertScope(requestScopes),
        };
        setSubControls(true);
      }
    } else {
      dataScopeSurvey = {
        ...dataScopeSurvey,
        access: DataScopeAccess.None,
      };
    }

    await updateDataScopeSurvey(dataScopeSurvey);
  };

  const handleChangePacks = async (scopes: RequestScope[]) => {
    if (scopes.length === 0) {
      addSiteError(`You are unable to deselect all ${PACK.PLURAL}`);
      return;
    }

    const dataScopeSurvey = {
      ...getDataScopeSurvey(dataScopeTemplate),
      scope: convertScope(scopes),
    };

    await updateDataScopeSurvey(dataScopeSurvey);
  };

  const handleChangeAllPacks = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    let dataScopeSurvey = {
      ...getDataScopeSurvey(dataScopeTemplate),
      access: checked ? DataScopeAccess.Full : DataScopeAccess.Partial,
    };

    // if current share packs is empty then select all packs as default
    if (!checked && requestScopes.length === 0) {
      dataScopeSurvey = {
        ...dataScopeSurvey,
        scope: convertScope(initiativeRequestScopes),
      };
    }

    await updateDataScopeSurvey(dataScopeSurvey);
  };

  const handleShareScopeViews = async (scopeView: DataShareScopeView) => {
    let newScopeViews = [...shareScopeViews];
    const existedIndex = newScopeViews.findIndex((item) => item === scopeView);
    if (existedIndex < 0) {
      newScopeViews = [...newScopeViews, scopeView];
    } else {
      newScopeViews = newScopeViews.filter((item) => item !== scopeView);
    }
    const dataScopeSurvey = {
      ...getDataScopeSurvey(dataScopeTemplate),
      views: newScopeViews,
    };

    await updateDataScopeSurvey(dataScopeSurvey);
  };

  return (
    <div className='data-share-permissions'>
      <div className='d-flex justify-content-between'>
        <div className='d-flex flex-column'>
          <h4>Automatic access rules</h4>
          <div className='text-md text-ThemeTextDark'>
            <span>
              All Portfolio Tracker requests will <br /> be accepted with these permissions
            </span>
          </div>
        </div>
        <div className='d-flex align-items-start gap-4 mt-4'>
          <FormGroup switch className='d-flex'>
            <Input
              id={'template-can-access-data'}
              type='switch'
              className='mr-2'
              checked={accessData}
              onChange={changeAccessData}
            />
            <label htmlFor={'template-can-access-data'} className='text-md'>
              Can access data
            </label>
          </FormGroup>
          <FormGroup switch className='d-flex'>
            <Input
              id={`template-${DataShareScopeView.Messaging}`}
              type='switch'
              className='mr-2'
              onChange={() => handleShareScopeViews(DataShareScopeView.Messaging)}
              checked={shareScopeViews.includes(DataShareScopeView.Messaging)}
            />
            <label htmlFor={`template-${DataShareScopeView.Messaging}`} className='d-flex flex-column'>
              <span className='text-md'>Can direct message</span>
              <span className='text-xs text-ThemeTextMedium'>(DMs will go to all Adminstrators)</span>
            </label>
          </FormGroup>
        </div>
      </div>

      {accessData ? (
        <div className='w-100'>
          <Collapse isOpen={openSubControls}>
            <div className='d-flex justify-content-center mt-3'>
              <div className='advanced-settings-divider' />
            </div>

            <Row className='pt-4'>
              <Col xs={12} md={5} className='d-flex flex-column text-ThemeTextMedium'>
                <Title>Access to</Title>
                <div className='d-flex align-items-center mt-2'>
                  <Input
                    id={`template-${DataShareTarget.CompletedSurveys}`}
                    type='radio'
                    name={'template-shareTarget'}
                    className='mr-2'
                    onChange={() => setShareTarget(DataShareTarget.CompletedSurveys)}
                    checked={shareTarget === DataShareTarget.CompletedSurveys}
                  />
                  <label htmlFor={`template-${DataShareTarget.CompletedSurveys}`}>
                    Data from Completed {SURVEY.PLURAL}
                    <SimpleTooltip text={TOOLTIP.completedSurveys}>
                      <i className='fa-light fa-circle-info ml-1' />
                    </SimpleTooltip>
                  </label>
                </div>
                <div className='d-flex align-items-center mt-2'>
                  <SimpleTooltip text={TOOLTIP.assuredData}>
                    <Input
                      id={`template-${DataShareTarget.OnlyAssuredData}`}
                      type='radio'
                      name={'template-shareTarget'}
                      className='mr-2 background-ThemeBgDark'
                      onChange={() => setShareTarget(DataShareTarget.OnlyAssuredData)}
                      checked={shareTarget === DataShareTarget.OnlyAssuredData}
                      disabled
                    />
                  </SimpleTooltip>
                  <label
                    htmlFor={`template-${DataShareTarget.OnlyAssuredData}`}
                    className={classNames({ 'text-ThemeTextLight': true })}
                  >{`Only ‘Assured’ data from Completed ${SURVEY.PLURAL}`}</label>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <DataScopeGroups
                  htmlId={'template-all-packs'}
                  onChange={handleChangeAllPacks}
                  isFullAccess={shareAllPacks}
                  requestScopes={requestScopes}
                  handleChangePacks={handleChangePacks}
                  initiativeRequestScopes={initiativeRequestScopes}
                />
              </Col>

              <Col xs={12} md={3} className='d-flex flex-column text-ThemeTextMedium'>
                <Title>Access to pages</Title>
                <FormGroup switch className='d-flex align-items-center mt-2'>
                  <Input
                    id={`template-${DataShareScopeView.Survey}`}
                    type='switch'
                    className='mr-2'
                    onChange={() => handleShareScopeViews(DataShareScopeView.Survey)}
                    checked={shareScopeViews.includes(DataShareScopeView.Survey)}
                  />
                  <label htmlFor={`template-${DataShareScopeView.Survey}`}>{SURVEY.CAPITALIZED_SINGULAR} overview</label>
                </FormGroup>
                <FormGroup switch className='d-flex align-items-center mt-2'>
                  <Input
                    id={`template-${DataShareScopeView.Insights}`}
                    type='switch'
                    className='mr-2'
                    onChange={() => handleShareScopeViews(DataShareScopeView.Insights)}
                    checked={shareScopeViews.includes(DataShareScopeView.Insights)}
                  />
                  <label htmlFor={`template-${DataShareScopeView.Insights}`}>
                    Insights
                    <SimpleTooltip text={TOOLTIP.insights}>
                      <i className='fa-light fa-circle-info ml-1' />
                    </SimpleTooltip>
                  </label>
                </FormGroup>
                <FormGroup switch className='d-flex align-items-center mt-2'>
                  <Input
                    id={`template-${DataShareScopeView.Downloads}`}
                    type='switch'
                    className='mr-2'
                    onChange={() => handleShareScopeViews(DataShareScopeView.Downloads)}
                    checked={shareScopeViews.includes(DataShareScopeView.Downloads)}
                  />
                  <label htmlFor={`template-${DataShareScopeView.Downloads}`}>Downloads</label>
                </FormGroup>
              </Col>
            </Row>
          </Collapse>

          <div className='d-flex justify-content-end mt-4'>
            <Button onClick={toggleSubControls} className='text-sm text-ThemeTextMedium px-2 border-0 bg-transparent'>
              Advanced settings{' '}
              <i className={`fa-light ${openSubControls ? 'fa-angle-up' : 'fa-angle-down'} text-sm ml-2`}></i>
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
